import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/authcontext";
import { isOwnerOrFinance } from "../../utils/role-check";
import ProjectForm from "./ProjectFormContainer";
import ProjectsList from "./ProjectsList";
import { useProjects } from "../../hooks/useProjects";
import StyledModal from "../Modal/StyledModalView";
import {useFlash} from "../../context/flashcontext";

function ProjectsContainer({ userId }) {
    const { t } = useTranslation();
    const [showForm, setShowForm] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [projectToDelete, setProjectToDelete] = React.useState(null);
    const [showArchive, setShowArchive] = useState(false);
    const navigate = useNavigate();
    const { auth, checkAuth } = useAuth();
    const { showFlash } = useFlash();

    const {
        projects,
        loading,
        error,
        totalProfit,
        fetchProjects,
        deleteProject
    } = useProjects(userId);
    React.useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Split projects into active and archived
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of today for date-only comparison

    const activeProjects = (projects || []).filter(p => {
        if (!p.end_date) return true; // No end date = active
        const endDate = new Date(p.end_date);
        endDate.setHours(0, 0, 0, 0); // Set to start of day for comparison
        return endDate > now; // Active only if end date is in the future
    });

    const archivedProjects = (projects || []).filter(p => {
        if (!p.end_date) return false; // No end date = not archived
        const endDate = new Date(p.end_date);
        endDate.setHours(0, 0, 0, 0); // Set to start of day for comparison
        return endDate <= now; // Archived if end date is today or past
    });

    // Only allow eigenaar and financieel medewerker to see archived projects
    const canViewArchive = isOwnerOrFinance(auth.role);

    const handleViewProject = (id) => {
        navigate(`/projects/${id}`);
    };

    const handleDeleteProject = (id) => {
        setProjectToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDeleteProject = async () => {
        if (projectToDelete) {
            await deleteProject(projectToDelete);
            setShowDeleteModal(false);
            setProjectToDelete(null);
        }
    };

    const cancelDeleteProject = () => {
        setShowDeleteModal(false);
        setProjectToDelete(null);
    };

    const handleViewProjectTimesheets = (id) => {
        navigate(`/projects/timesheets/${id}`);
    };

    // UI: Toggle between active and archive
    return (
        <div className="projects-container">
            <div className="list-header" >
            <h2>{showForm ? t('projects.addProject') : t('projects.title')}</h2>
            {!userId && isOwnerOrFinance(auth.role) && !showForm && (
                <button type="button" className="btn-add" onClick={() => setShowForm(true)}>
                    {t('projects.addProject')}
                </button>
            )}
            </div>
            <StyledModal
                isOpen={showForm}
                onClose={() => setShowForm(false)}
                title={t('projects.addProject')}
                confirmText={null}
                closeText={null}
            >
                <ProjectForm
                    onSuccess={() => {
                        setShowForm(false);
                        fetchProjects(userId);
                    }}
                    onCancel={() => setShowForm(false)}
                />
            </StyledModal>
            {canViewArchive && (
                <div className="projects-toggle-bar">
                    <button
                        className={!showArchive ? "toggle-btn active" : "toggle-btn"}
                        onClick={() => setShowArchive(false)}
                    >
                        {t('projects.activeProjectsTab')}
                    </button>
                    <button
                        className={showArchive ? "toggle-btn active" : "toggle-btn"}
                        onClick={() => setShowArchive(true)}
                    >
                        {t('projects.archivedProjects')}
                    </button>
                </div>
            )}
            {!showArchive ? (
                <ProjectsList
                    projects={activeProjects}
                    loading={loading}
                    error={error}
                    userId={userId}
                    auth={auth}
                    onViewProject={handleViewProject}
                    onDeleteProject={handleDeleteProject}
                    onViewProjectTimesheets={handleViewProjectTimesheets}
                    totalProfit={totalProfit}
                    isArchive={false}
                    showFlash={showFlash}
                />
            ) : canViewArchive ? (
                <ProjectsList
                    projects={archivedProjects}
                    loading={loading}
                    error={error}
                    userId={userId}
                    auth={auth}
                    onViewProject={handleViewProject}
                    onDeleteProject={handleDeleteProject}
                    onViewProjectTimesheets={handleViewProjectTimesheets}
                    totalProfit={0}
                    isArchive={true}
                    showFlash={showFlash}
                />
            ) : null}
            <StyledModal
                isOpen={showDeleteModal}
                onClose={cancelDeleteProject}
                title={t('projects.deleteProject')}
            >
                <p>{t('projects.confirmDelete')}</p>
                <div className="modal-footer">
                    <button type="button" className="modal-btn modal-btn-secondary" onClick={cancelDeleteProject}>
                        {t('common.cancel')}
                    </button>
                    <button type="button" className="modal-btn modal-btn-danger" onClick={confirmDeleteProject}>
                        {t('common.delete')}
                    </button>
                </div>
            </StyledModal>
        </div>
    );
}

export default ProjectsContainer;

ProjectsContainer.propTypes = {
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
